import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api'
import { Column } from 'primereact/column'
import { DataTableFilterMeta } from 'primereact/datatable'

import { SearchField } from '../../../../../components/SearchField'
import {
  DateBodyTemplate,
  DateFilterTemplate,
  EInvoiceStatusBodyTemplate,
  EInvoiceStatusFilterTemplate,
  InvoiceTypeBodyTemplate,
  InvoiceTypeFilterTemplate,
  PriceBodyTemplate,
  PriceFilterTemplate,
  TestAndSourceBodyTemplate,
  TestAndSourceFilterTemplate,
} from '../../../../../components/tableTemplates'
import { EInvoiceDto } from '../../../../../data/models'
import { EInvoiceMetadata } from '../../../../../data/models/_generatedUblInvoice'
import { useSearchParam } from '../../../../../hooks'
import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config/context'
import { useAllEInvoices } from '../../../data'
import { TestAndSourceFilter, testAndSourceFilterService } from '../../../utils'
import { convertEInvoiceDates } from '../../utils'

const TOGGLEABLE_FIELDS = [
  'Meta',
  'Payload.InvoiceTypeCode.Value',
  'Payload.AccountingCustomerParty.Party.PartyName.Name.Value',
  'Payload.IssueDate.Value',
  'Payload.LegalMonetaryTotal.PayableAmount.Value.Value',
  'Meta.file.fileName',
  'Meta.file.uploadedBy',
  'Meta.file.uploadedAt',
]

const STORAGE_KEY = 'e-invoices-visible-columns'

export const Table: React.FC = () => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const [globalFilterValue, setGlobalFilterValue] = useSearchParam('search')
  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  const { visibleFields, navigationColumnHeader } = useColumnToggle(
    config.eInvoices.eInvoice.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
  )

  const { data, isLoading } = useAllEInvoices()

  useEffect(() => {
    initFilters()
  }, [])

  const initFilters = () => {
    setFilters(defaultFilters)
  }

  useEffect(() => {
    let _filters = { ...filters }

    // @ts-ignore
    _filters['global'].value = globalFilterValue

    setFilters(_filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterValue])

  const clearFilter = () => {
    initFilters()
  }

  const headerActions = (
    <SearchField
      searchValue={globalFilterValue}
      setSearchValue={setGlobalFilterValue}
      placeholder={t('eInvoiceOverview.table.search.placeholder')}
      width={23}
      classNameInput="surface-50"
    />
  )

  const show = (field: string) =>
    !TOGGLEABLE_FIELDS.includes(field) || visibleFields.includes(field)

  const columns: JSX.Element[] = [
    show('Meta') ? (
      <Column
        key="Meta"
        field="Meta"
        header={t('eInvoiceOverview.table.columns.testAndSource.header')}
        body={(rowData: EInvoiceDto) =>
          TestAndSourceBodyTemplate(
            rowData?.Meta?.controlInformation?.trialFlag,
            rowData?.Meta?.controlInformation?.processingType,
          )
        }
        filter
        filterElement={TestAndSourceFilterTemplate}
        filterMatchMode={FilterMatchMode.EQUALS}
        showFilterMatchModes={false}
        align="center"
      />
    ) : null,
    <Column
      key="InvoiceNumber"
      field="Payload.ID.Value.Value"
      header={t('eInvoiceOverview.table.columns.invoiceNumber.header')}
      filter
      filterMatchMode={FilterMatchMode.STARTS_WITH}
      filterPlaceholder={t(
        'eInvoiceOverview.table.columns.invoiceNumber.filterPlaceholder',
      )}
      sortable
    />,
    show('Payload.InvoiceTypeCode.Value') ? (
      <Column
        key="InvoiceTypeCode"
        field="Payload.InvoiceTypeCode.Value"
        header={t('eInvoiceOverview.table.columns.invoiceType.header')}
        body={(rowData: EInvoiceDto) =>
          InvoiceTypeBodyTemplate(rowData.Payload?.InvoiceTypeCode)
        }
        filter
        filterMatchMode={FilterMatchMode.EQUALS}
        filterElement={InvoiceTypeFilterTemplate}
        filterPlaceholder={t(
          'eInvoiceOverview.table.columns.invoiceType.filterPlaceholder',
        )}
        sortable
      />
    ) : null,
    show('Payload.AccountingCustomerParty.Party.PartyName.Name.Value') ? (
      <Column
        key="AccountingCustomerParty"
        field="Payload.AccountingCustomerParty.Party.PartyName.Name.Value"
        header={t('eInvoiceOverview.table.columns.recipientTradeName.header')}
        filter
        filterMatchMode={FilterMatchMode.STARTS_WITH}
        filterPlaceholder={t(
          'eInvoiceOverview.table.columns.recipientTradeName.filterPlaceholder',
        )}
        sortable
      />
    ) : null,
    show('Payload.IssueDate.Value') ? (
      <Column
        key="InvoiceDate"
        field="Payload.IssueDate.Value"
        header={t('eInvoiceOverview.table.columns.invoiceDate.header')}
        body={(rowData: EInvoiceDto) =>
          DateBodyTemplate(rowData.Payload?.IssueDate?.Value ?? undefined)
        }
        filter
        filterElement={DateFilterTemplate}
        filterMatchMode={FilterMatchMode.DATE_IS}
        sortable
      />
    ) : null,
    show('Payload.LegalMonetaryTotal.PayableAmount.Value.Value') ? (
      <Column
        key="GrossPrice"
        field="Payload.LegalMonetaryTotal.PayableAmount.Value.Value"
        header={t('eInvoiceOverview.table.columns.grossPrice.header')}
        body={rowData =>
          PriceBodyTemplate(
            rowData.Payload.LegalMonetaryTotal.PayableAmount.Value.Value,
            { currency: 'EUR' },
          )
        }
        filter
        filterMatchMode={FilterMatchMode.GREATER_THAN}
        filterElement={options =>
          PriceFilterTemplate(
            options,
            ' €',
            t('eInvoiceOverview.table.columns.grossPrice.filterPlaceholder'),
          )
        }
        filterPlaceholder={t(
          'eInvoiceOverview.table.columns.grossPrice.filterPlaceholder',
        )}
        align="right"
        dataType="numeric"
        sortable
      />
    ) : null,
    show('Meta.file.fileName') ? (
      <Column
        key="DocumentName"
        field="Meta.file.fileName"
        header={t('eInvoiceOverview.table.columns.documentName.header')}
        filter
        filterMatchMode={FilterMatchMode.STARTS_WITH}
        filterPlaceholder={t(
          'eInvoiceOverview.table.columns.documentName.filterPlaceholder',
        )}
        sortable
      />
    ) : null,
    show('Meta.file.uploadedBy') ? (
      <Column
        key="CreatedBy"
        field="Meta.file.uploadedBy"
        header={t('eInvoiceOverview.table.columns.createdBy.header')}
        filter
        filterMatchMode={FilterMatchMode.STARTS_WITH}
        filterPlaceholder={t(
          'eInvoiceOverview.table.columns.createdBy.filterPlaceholder',
        )}
        sortable
      />
    ) : null,
    show('Meta.file.uploadedAt') ? (
      <Column
        key="CreatedAt"
        field="Meta.file.uploadedAt"
        header={t('eInvoiceOverview.table.columns.createdAt.header')}
        body={rowData =>
          DateBodyTemplate(rowData.Meta.file.uploadedAt, undefined, true)
        }
        filter
        filterMatchMode={FilterMatchMode.DATE_IS}
        filterElement={DateFilterTemplate}
        sortable
      />
    ) : null,
    <Column
      key="Status"
      field="Meta.file.status"
      header={t('eInvoiceOverview.table.columns.status.header')}
      body={rowData => EInvoiceStatusBodyTemplate(rowData.Meta.file.status)}
      filter
      filterMatchMode={FilterMatchMode.EQUALS}
      filterElement={EInvoiceStatusFilterTemplate}
      filterPlaceholder={t(
        'eInvoiceOverview.table.columns.status.filterPlaceholder',
      )}
      sortable
      frozen
      alignFrozen="right"
    />,
  ].filter((c): c is JSX.Element => c !== null)

  return (
    <ConfigurableTable
      headerTitle={t('eInvoiceOverview.table.title')}
      config={config.eInvoices.eInvoice.table}
      data={convertEInvoiceDates(data)}
      dataKey="id"
      stateKey="table-e-invoices-table"
      headerActions={headerActions}
      defaultFilters={filters}
      updateDefaultFilters={e => setFilters(e as any)}
      clearDefaultFilter={clearFilter}
      globalFilterFields={['Payload.ID.Value.Value']}
      setGlobalFilterValue={setGlobalFilterValue}
      sortField="Payload.InvoiceDate"
      sortOrder={-1}
      filterDisplay="menu"
      link={'e-invoices'}
      isLoading={isLoading}
      navigationColumnHeader={navigationColumnHeader}
    >
      {columns}
    </ConfigurableTable>
  )
}

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  Meta: {
    value: null,
    matchMode: FilterMatchMode.CUSTOM,
  },
  'Payload.ID.Value.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Payload.InvoiceTypeCode.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Payload.AccountingCustomerParty.Party.PartyName.Name.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Payload.IssueDate.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  'Payload.LegalMonetaryTotal.PayableAmount.Value.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.GREATER_THAN }],
  },
  'Meta.file.fileName': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Meta.file.uploadedBy': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Meta.file.uploadedAt': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  'Meta.file.status': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
}

FilterService.register(
  'custom_Meta',
  (value: EInvoiceMetadata, filters: TestAndSourceFilter) => {
    return testAndSourceFilterService(
      value.controlInformation.trialFlag,
      value.controlInformation.processingType,
      filters,
    )
  },
)
